import { type SparkConfig, type SparkCredential, useSparkConfig } from "@/hooks/useSparkConfig";
import { formatRelativeTime } from "@/lib/relativeTime";
import { QueryView } from "@simplicity/ui";

// ─── Sub-components ───────────────────────────────────────────────────────────

function SparkStatus({ config }: { config: SparkConfig }) {
  if (config.effectiveEnabled) {
    return (
      <>
        <p className="text-success font-medium">On</p>
        <p className="text-sm text-muted-foreground mt-1">
          SimplySpark dashboards are visible to this tenant.
        </p>
      </>
    );
  }
  if (config.sparkEnabled) {
    return (
      <>
        <p className="text-warning font-medium">Needs BigQuery setup</p>
        <p className="text-sm text-muted-foreground mt-1">
          SimplySpark is switched on for this tenant, but it has no BigQuery credential, so it stays
          off until one is added.
        </p>
      </>
    );
  }
  return (
    <>
      <p className="text-muted-foreground font-medium">Off</p>
      <p className="text-sm text-muted-foreground mt-1">
        SimplySpark dashboards are hidden for this tenant. Hidden dashboards are kept, not deleted.
      </p>
    </>
  );
}

function DateCell({ value }: { value: string }) {
  return (
    <>
      <div>{formatRelativeTime(value)}</div>
      <div className="text-xs">{new Date(value).toLocaleDateString()}</div>
    </>
  );
}

function CredentialTable({ credentials }: { credentials: SparkCredential[] }) {
  if (credentials.length === 0) {
    return <p className="text-sm text-muted-foreground">No BigQuery credential configured.</p>;
  }
  return (
    <div className="overflow-auto">
      <table className="w-full text-sm border border-border rounded">
        <thead>
          <tr className="border-b border-border bg-muted text-muted-foreground text-left">
            <th className="px-3 py-2 font-medium">BigQuery project</th>
            <th className="px-3 py-2 font-medium">Datasets</th>
            <th className="px-3 py-2 font-medium">Reader service account</th>
            <th className="px-3 py-2 font-medium">Secret</th>
            <th className="px-3 py-2 font-medium">Created</th>
            <th className="px-3 py-2 font-medium">Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {credentials.map((c) => (
            <tr key={c.id}>
              <td className="px-3 py-2">{c.bqProjectId}</td>
              <td className="px-3 py-2">{c.datasets.join(", ") || "—"}</td>
              <td className="px-3 py-2 break-all">{c.readerServiceAccount ?? "—"}</td>
              <td className="px-3 py-2 break-all">{c.secretName}</td>
              <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                <DateCell value={c.createdAt} />
              </td>
              <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                <DateCell value={c.updatedAt} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SparkConfigDetails({ config }: { config: SparkConfig }) {
  return (
    <div className="space-y-6">
      <SparkStatus config={config} />
      <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Tenant setting</dt>
        <dd>{config.sparkEnabled ? "On" : "Off"}</dd>
        <dt className="text-muted-foreground">SimplySpark dashboards</dt>
        <dd>{config.sparkDashboardCount}</dd>
      </dl>
      <div>
        <h3 className="text-base font-semibold mb-3">BigQuery credentials</h3>
        <CredentialTable credentials={config.credentials} />
      </div>
      <p className="text-xs text-muted-foreground">This view is read-only.</p>
    </div>
  );
}

// ─── Main section ─────────────────────────────────────────────────────────────

export function SparkConfigSection({ tenantId }: { tenantId: string }) {
  const query = useSparkConfig(tenantId);

  return (
    <section aria-labelledby="spark-config-heading">
      <h2 id="spark-config-heading" className="text-lg font-semibold mb-4">
        SimplySpark
      </h2>
      <QueryView
        query={query}
        error={(err) => (
          <p className="text-sm text-destructive">
            Could not load SimplySpark settings. {err.message}
          </p>
        )}
      >
        {(config) => <SparkConfigDetails config={config} />}
      </QueryView>
    </section>
  );
}
